import { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@clerk/clerk-react";
import { useBlocker } from "react-router-dom";
import { PDFDocument } from "pdf-lib";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { SIGNATURE_PLACEMENTS } from "@/reports/shared_utils/signaturePlacements";
import { findUnsupportedChars, describeChar } from "@/reports/shared_utils/sanitizeForPdf";

/**
 * Shared hook that encapsulates all report form state management:
 * - Form setup (react-hook-form + zod)
 * - Data loading (from API or Report Folder defaults)
 * - Live PDF regeneration with field highlighting
 * - Delta auto-save: 2.5s debounce after last change (reportId only)
 * - beforeunload guard for unsaved changes
 * - PDF scroll target computation from template field positions
 * - Step navigation with manual completion tracking
 *
 * Config:
 *   formSchema         - Zod schema for react-hook-form
 *   defaultValues      - Default form values
 *   fillPDF            - async (values, highlightRect?) => objectURL
 *   templateUrl        - URL to the PDF template (Vite static asset)
 *   STEPS              - Array of { id, title } step definitions
 *   STEP_FIELDS        - { [stepId]: string[] } fields per step
 *   STEP_ANCHOR_FIELDS - { [stepId]: pdfFieldName } for scroll targeting
 *   SCHEMA_TO_PDF_FIELD - { schemaField: pdfFieldName }
 *   getPageIndex       - (schemaField: string) => number (0-based page index)
 *   getInitialPage     - (stepId: number) => number (1-based page number for initial scroll targets)
 *
 * Props (passed through from the Page component):
 *   reportId           - MongoDB report ID (null for demo / defaults mode)
 *   defaultsGroupId    - Report Folder ID when editing Report Folder defaults
 */
export function useReportForm(config, props) {
  const {
    formSchema,
    defaultValues,
    fillPDF,
    templateUrl,
    STEPS,
    STEP_FIELDS,
    STEP_ANCHOR_FIELDS,
    SCHEMA_TO_PDF_FIELD,
    getPageIndex,
    getInitialPage,
    reportTypeKey,
  } = config;

  const { reportId, defaultsGroupId, groupGrade, logos, logosVersion, isOwner, ownSignature } = props;

  // Resolved signature images for this report ({ teacher, principal } data URIs).
  // Fetched here so any viewer (owner, shared, admin) sees whoever has signed.
  const [localSigs, setLocalSigs] = useState({ teacher: null, principal: null });
  const signedState = { teacher: !!localSigs.teacher, principal: !!localSigs.principal };

  // Build signature image overlays. Rendered the same way as logos (positioned,
  // aspect-contained images) so they just append to the logos array passed to fill.
  const buildSignatureOverlays = useCallback(() => {
    const placements = SIGNATURE_PLACEMENTS[reportTypeKey] || {};
    const out = [];
    for (const role of ["teacher", "principal"]) {
      if (localSigs[role] && placements[role]) {
        const { page, x, y, width, height } = placements[role];
        out.push({ data: localSigs[role], page, x, y, width, height });
      }
    }
    return out;
  }, [reportTypeKey, localSigs]);

  // Combined overlay array (folder logos + signature images) for fill. Held in a
  // ref so the stable fillWithLogos wrapper always reads the latest set.
  const overlaysRef = useRef([]);
  overlaysRef.current = [...(logos || []), ...buildSignatureOverlays()];

  const [currentStep, setCurrentStep] = useState(1);
  const [filledPDF, setFilledPDF] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [showPostDownloadModal, setShowPostDownloadModal] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const hasSavedOnce = useRef(false);
  const [scrollTargets, setScrollTargets] = useState(
    Object.fromEntries(STEPS.map((s) => [s.id, { page: getInitialPage(s.id), topFraction: 0 }]))
  );

  const changedFieldsRef = useRef(new Set());
  // Every unsupported character seen this session. Surfaced in a single,
  // coalesced toast (debounced + fixed id) so 10 bad characters produce one
  // notification listing them all, not 10 stacked toasts.
  const warnedCharsRef = useRef(new Set());
  const unsupportedToastTimerRef = useRef(null);
  const saveDebounceRef = useRef(null);
  const regenDebounceRef = useRef(null);
  const fieldPositionsRef = useRef({});
  const lastChangedFieldRef = useRef(null);
  const formPaneRef = useRef(null);
  const mountedRef = useRef(true);
  const flushSaveRef = useRef(null);
  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Inject the current logos into every fill call. Stable identity (only depends
  // on the module's fillPDF), so effects below don't churn when logos load.
  const fillWithLogos = useCallback(
    (values, highlightRect = null) => fillPDF(values, highlightRect, overlaysRef.current),
    [fillPDF]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Hold the latest captures (getToken, form helpers, fillPDF, groupGrade) in a ref
  // so the load effect below can read fresh values without re-running when their
  // identities change. The effect's only triggers are reportId / defaultsGroupId.
  const loadCaptures = useRef({ getToken, form, fillPDF: fillWithLogos, groupGrade });
  loadCaptures.current = { getToken, form, fillPDF: fillWithLogos, groupGrade };

  // Load saved data + generate initial PDF
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const { getToken: capGetToken, form: capForm, fillPDF: capFillPDF, groupGrade: capGroupGrade } = loadCaptures.current;
    if (reportId) {
      capGetToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        })
          .then((r) => r.json())
          .then((json) => {
            if (!mountedRef.current) return;
            const base = json.data && Object.keys(json.data).length > 0 ? json.data : capForm.getValues();
            const values = (!base.grade && capGroupGrade) ? { ...base, grade: capGroupGrade } : base;
            capForm.reset(values);
            capFillPDF(values).then((url) => { if (mountedRef.current) setFilledPDF(url); });
          })
          .catch((err) => {
            if (err.name === "AbortError") return;
            capFillPDF(capForm.getValues()).then((url) => { if (mountedRef.current) setFilledPDF(url); });
          })
      );
    } else if (defaultsGroupId) {
      capGetToken().then((token) =>
        fetch(`${BACKEND_URL}api/report-folders/${defaultsGroupId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        })
          .then((r) => r.json())
          .then((json) => {
            if (!mountedRef.current) return;
            const values = { ...capForm.getValues(), ...(json.reportDefaults || {}) };
            capForm.reset(values);
            capFillPDF(values).then((url) => { if (mountedRef.current) setFilledPDF(url); });
          })
          .catch((err) => {
            if (err.name === "AbortError") return;
            capFillPDF(capForm.getValues()).then((url) => { if (mountedRef.current) setFilledPDF(url); });
          })
      );
    } else {
      capFillPDF(capForm.getValues()).then((url) => { if (mountedRef.current) setFilledPDF(url); });
    }
    return () => controller.abort();
  }, [reportId, defaultsGroupId]);

  // Fetch resolved signature images (teacher/principal) for this report.
  useEffect(() => {
    if (!reportId) return;
    const ac = new AbortController();
    getToken().then((token) =>
      fetch(`${BACKEND_URL}api/reports/${reportId}/signatures`, {
        headers: { Authorization: `Bearer ${token}` }, signal: ac.signal,
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => {
          if (json && mountedRef.current) {
            setLocalSigs({ teacher: json.teacher ?? null, principal: json.principal ?? null });
          }
        })
        .catch(() => { /* signatures are non-critical */ })
    );
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Build scroll targets + field bounding boxes from PDF template
  useEffect(() => {
    async function buildTargets() {
      try {
        const bytes = await fetch(templateUrl).then((r) => r.arrayBuffer());
        const pdfDoc = await PDFDocument.load(bytes);
        const pdfForm = pdfDoc.getForm();
        const pages = pdfDoc.getPages();

        const updated = {};
        for (const [stepStr, fieldName] of Object.entries(STEP_ANCHOR_FIELDS)) {
          const step = Number(stepStr);
          const pageIdx = getInitialPage(step) - 1;
          try {
            const f = pdfForm.getField(fieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) throw new Error("no widgets");
            const rect = widgets[0].getRectangle();
            const pageH = pages[pageIdx].getHeight();
            const topFromTop = Math.max(0, pageH - rect.y - rect.height - 40);
            updated[step] = { page: pageIdx + 1, topFraction: Math.min(0.95, topFromTop / pageH) };
          } catch {
            updated[step] = { page: pageIdx + 1, topFraction: 0 };
          }
        }
        if (mountedRef.current) setScrollTargets((prev) => ({ ...prev, ...updated }));

        const positions = {};
        for (const [schemaField, pdfFieldName] of Object.entries(SCHEMA_TO_PDF_FIELD)) {
          const pageIdx = getPageIndex(schemaField);
          try {
            const f = pdfForm.getField(pdfFieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) continue;
            const rect = widgets[0].getRectangle();
            positions[schemaField] = { pageIndex: pageIdx, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
          } catch { /* field not in template */ }
        }
        fieldPositionsRef.current = positions;
      } catch (e) {
        console.error("Failed to build PDF scroll targets:", e);
      }
    }
    buildTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch form changes: mark dirty, track changed fields, debounce PDF regen + save
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      // name is undefined when form.reset() fires - that's a data load, not a user change
      if (!name) return;

      changedFieldsRef.current.add(name);
      lastChangedFieldRef.current = name;

      if (mountedRef.current) setIsDirty(true);

      // Warn about characters that can't be rendered in the PDF. The toast only
      // fires when a NEW unsupported character first appears (warnedCharsRef
      // only grows), so editing or fixing existing ones never re-triggers it -
      // a teacher can spend minutes correcting 10 characters without a single
      // extra toast. The list shown reflects what is CURRENTLY in the form, so
      // already-fixed characters drop off. One debounced toast, fixed id.
      if (name !== "_completedFields") {
        const value = form.getValues(name);
        if (typeof value === "string" && value) {
          const before = warnedCharsRef.current.size;
          for (const ch of findUnsupportedChars(value)) warnedCharsRef.current.add(ch);
          if (warnedCharsRef.current.size > before) {
            if (unsupportedToastTimerRef.current) clearTimeout(unsupportedToastTimerRef.current);
            unsupportedToastTimerRef.current = setTimeout(() => {
              const present = new Set();
              for (const v of Object.values(form.getValues())) {
                if (typeof v === "string") {
                  findUnsupportedChars(v).forEach((c) => present.add(c));
                } else if (Array.isArray(v)) {
                  v.forEach((it) => {
                    if (typeof it === "string") findUnsupportedChars(it).forEach((c) => present.add(c));
                  });
                }
              }
              if (!present.size) return; // all offending characters already removed
              const chars = [...present];
              const list = chars.map(describeChar).join(", ");
              toast.warning(
                chars.length === 1
                  ? `${list} can't be shown in the PDF - it'll appear as [?]`
                  : "Some characters can't be shown in the PDF - they'll appear as [?]",
                {
                  id: "pdf-unsupported-chars",
                  description:
                    (chars.length === 1 ? "" : `${list}. `) +
                    "Need them in your PDFs? Email contact@easyreports.ca.",
                  duration: 8000,
                }
              );
            }, 600);
          }
        }
      }

      // PDF regen (skip for _completedFields - no PDF field for it)
      if (name !== "_completedFields") {
        if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
        if (mountedRef.current) setIsPdfGenerating(true);
        regenDebounceRef.current = setTimeout(async () => {
          const changedField = lastChangedFieldRef.current;
          const highlightRect = changedField !== "_completedFields"
            ? (fieldPositionsRef.current[changedField] ?? null)
            : null;
          const url = await fillWithLogos(form.getValues(), highlightRect);
          if (mountedRef.current) {
            setFilledPDF(url);
            setIsPdfGenerating(false);
          }
        }, 500);
      }

      // Auto-save debounce (reportId only; defaults mode has no auto-save)
      if (reportId) {
        if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = setTimeout(() => {
          flushSaveRef.current?.();
        }, 2500);
      }
    });
    return () => {
      subscription.unsubscribe();
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
      if (unsupportedToastTimerRef.current) clearTimeout(unsupportedToastTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, reportId]);

  // Flush save (delta PUT - only changed fields)
  flushSaveRef.current = () => {
    if (!changedFieldsRef.current.size) return;
    if (!reportId) return;

    // Snapshot and clear atomically; any changes during the request go into the now-empty ref
    const snapshot = new Set(changedFieldsRef.current);
    changedFieldsRef.current = new Set();

    if (mountedRef.current) {
      setIsDirty(false);
      setIsSaving(true);
    }

    const allValues = form.getValues();
    const delta = {};
    for (const key of snapshot) {
      delta[key] = allValues[key];
    }

    getToken().then((token) =>
      fetch(`${BACKEND_URL}api/reports/${reportId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ data: delta }),
      }).then((res) => {
        if (mountedRef.current) {
          setIsSaving(false);
          if (!res.ok) {
            // Restore snapshot on failure so changes aren't lost
            for (const key of snapshot) changedFieldsRef.current.add(key);
            setIsDirty(true);
          } else {
            hasSavedOnce.current = true;
          }
        }
      }).catch(() => {
        if (mountedRef.current) {
          setIsSaving(false);
          for (const key of snapshot) changedFieldsRef.current.add(key);
          setIsDirty(true);
        }
      })
    );
  };

  // Flush on unmount
  useEffect(() => {
    return () => { flushSaveRef.current?.(); };
  }, []);

  // beforeunload guard - browsers force a native dialog here; custom UI is not permitted
  useEffect(() => {
    const handler = (e) => {
      if (changedFieldsRef.current.size > 0 && reportId) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [reportId]);

  // In-app navigation guard - drives the custom UnsavedChangesModal in the shell
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) =>
      Boolean(reportId) &&
      changedFieldsRef.current.size > 0 &&
      currentLocation.pathname !== nextLocation.pathname,
    [reportId]
  );
  const blocker = useBlocker(shouldBlock);

  function triggerPdfRegen() {
    if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
    if (mountedRef.current) setIsPdfGenerating(true);
    regenDebounceRef.current = setTimeout(async () => {
      const url = await fillWithLogos(form.getValues(), null);
      if (mountedRef.current) {
        setFilledPDF(url);
        setIsPdfGenerating(false);
      }
    }, 50);
  }

  // Logos load asynchronously after first render. When the version changes
  // (i.e. they finished loading or were edited), regenerate the preview so they
  // appear. Skip the initial render - the load effect already drew with them.
  const logosSettledRef = useRef(false);
  useEffect(() => {
    if (!logosSettledRef.current) { logosSettledRef.current = true; return; }
    triggerPdfRegen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logosVersion]);

  // Regenerate the preview when signature state or the user's own signature
  // changes (sign/unsign, or the signature image finished loading). Skip initial.
  const sigSettledRef = useRef(false);
  const signaturesKey = `${signedState.teacher ? 1 : 0}${signedState.principal ? 1 : 0}`;
  useEffect(() => {
    if (!sigSettledRef.current) { sigSettledRef.current = true; return; }
    triggerPdfRegen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signaturesKey]);

  // Sign / unsign the current report. The image is pulled from the signer's
  // profile at render time; here we only flip the stored ref + timestamp.
  const [signBusy, setSignBusy] = useState(false);
  async function signAs(role) {
    if (!reportId) return;
    if (!ownSignature?.data) {
      toast.error("Set up your signature first (Settings → My Signature).");
      return;
    }
    setSignBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/reports/${reportId}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Could not sign the report.");
      }
      // Optimistically show the signer's own signature (the only role signable here).
      setLocalSigs((s) => ({ ...s, [role]: ownSignature.data }));
      toast.success("Report signed.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSignBusy(false);
    }
  }
  async function unsignAs(role) {
    if (!reportId) return;
    setSignBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/reports/${reportId}/unsign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) throw new Error("Could not remove the signature.");
      setLocalSigs((s) => ({ ...s, [role]: null }));
      toast.success("Signature removed.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSignBusy(false);
    }
  }

  // Navigation handlers
  function handleNext() {
    flushSaveRef.current?.();
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    formPaneRef.current?.scrollTo({ top: 0 });
  }

  function handlePrev() {
    flushSaveRef.current?.();
    setCurrentStep((s) => Math.max(s - 1, 1));
    formPaneRef.current?.scrollTo({ top: 0 });
  }

  function handleDownload() {
    flushSaveRef.current?.();
    if (!filledPDF) return;
    fillWithLogos(form.getValues(), null).then((cleanUrl) => {
      const { studentName, oen } = form.getValues();
      const fileName = [studentName, oen].filter(Boolean).join("_").replace(/\s+/g, " ").trim() || "report-card";
      const link = document.createElement("a");
      link.href = cleanUrl;
      link.setAttribute("download", `${fileName}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (!reportId && !defaultsGroupId) {
        setShowPostDownloadModal(true);
      }
    });
  }

  function toggleManualComplete(stepId) {
    const stepFields = STEP_FIELDS[stepId];
    const current = new Set(form.getValues("_completedFields") || []);
    const isComplete = stepFields.every((f) => current.has(f));
    if (isComplete) {
      stepFields.forEach((f) => current.delete(f));
    } else {
      stepFields.forEach((f) => current.add(f));
    }
    form.setValue("_completedFields", [...current]);
  }

  // Step completion tracking
  const watchedValues = form.watch();
  const completedFields = new Set(watchedValues._completedFields || []);
  const completedSteps = new Set(
    STEPS.filter((step) =>
      STEP_FIELDS[step.id].every((f) => completedFields.has(f))
    ).map((step) => step.id)
  );

  return {
    form,
    currentStep,
    setCurrentStep,
    filledPDF,
    isPdfGenerating,
    showPreview,
    setShowPreview,
    showPostDownloadModal,
    setShowPostDownloadModal,
    isDirty,
    isSaving,
    hasSavedOnce,
    scrollTargets,
    completedSteps,
    formPaneRef,
    handleNext,
    handlePrev,
    handleDownload,
    toggleManualComplete,
    triggerPdfRegen,
    flushSaveRef,
    blocker,
    // Signature controls (teacher self-sign in the editor)
    signature: {
      signedState,
      signAs,
      unsignAs,
      signBusy,
      canSignTeacher: Boolean(reportId && isOwner && SIGNATURE_PLACEMENTS[reportTypeKey]?.teacher),
      hasOwnSignature: Boolean(ownSignature?.data),
    },
  };
}
